import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import AnalyticsService from '../services/analyticsService';
import reportingService from '../services/reportingService';
import { fetchUserProfileDocument } from '../utils/userProfileLoad';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userOrganizations, setUserOrganizations] = useState([]);
  const [currentOrganizationId, setCurrentOrganizationId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState(false);
  const [reportingData, setReportingData] = useState({}); // New: reporting data cache
  const profileFetchIdRef = useRef(0);

  // Load reporting data for all user organizations
  const loadReportingData = useCallback(async (organizations) => {
    if (!organizations || organizations.length === 0) return;
    
    // Check if we already loaded data for these organizations using ref
    const hasDataForAllOrgs = organizations.every(orgId => loadedOrganizationsRef.current.has(orgId));
    
    if (hasDataForAllOrgs) {
      return; // Don't reload if we already have all the data
    }
    
    try {
      const data = {};
      
      // Load data for each organization in parallel
      const promises = organizations.map(async (orgId) => {
        try {
          const orgData = await reportingService.getOverview(orgId);
          data[orgId] = orgData;
          loadedOrganizationsRef.current.add(orgId); // Track that we loaded this org
        } catch (error) {
          console.error(`Error loading reporting data for ${orgId}:`, error);
          // Store empty data on error
          data[orgId] = {
            lifetimeStats: { lessonsAccessed: 0, logins: 0, promptsAccessed: 0, lifetimeUsers: 0 },
            organizationInfo: { name: 'Unknown', isActive: false, state: 'Unknown' },
            recentActivity: { totalInteractions: 0, contentInteractions: 0, userInteractions: 0 },
            timestamp: new Date().toISOString()
          };
          loadedOrganizationsRef.current.add(orgId); // Track even failed loads to prevent retrying
        }
      });
      
      await Promise.all(promises);
      
      // Store in state and sessionStorage
      setReportingData(data);
      sessionStorage.setItem('storykin_reporting_data', JSON.stringify(data));
      
      // Data loaded and cached successfully
    } catch (error) {
      console.error('Error loading reporting data:', error);
    }
  }, []); // Empty dependency array since we use refs for tracking

  // Refresh reporting data for a specific organization
  const refreshReportingData = async (organizationId) => {
    if (!organizationId) return;
    
    try {
      const orgData = await reportingService.getOverview(organizationId);
      
      setReportingData(prev => {
        const updated = { ...prev, [organizationId]: orgData };
        sessionStorage.setItem('storykin_reporting_data', JSON.stringify(updated));
        return updated;
      });
      
      console.log(`Reporting data refreshed for organization: ${organizationId}`);
      return orgData;
    } catch (error) {
      console.error(`Error refreshing reporting data for ${organizationId}:`, error);
      throw error;
    }
  };

  // Get reporting data for an organization (from cache or sessionStorage)
  const getReportingData = (organizationId) => {
    // First try memory cache
    if (reportingData[organizationId]) {
      return reportingData[organizationId];
    }
    
    // Fallback to sessionStorage
    try {
      const stored = sessionStorage.getItem('storykin_reporting_data');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed[organizationId]) {
          // Update memory cache
          setReportingData(parsed);
          return parsed[organizationId];
        }
      }
    } catch (error) {
      console.error('Error reading reporting data from sessionStorage:', error);
    }
    
    return null;
  };

  // Session management state
  const [currentSession, setCurrentSession] = useState(null);
  const activityTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const loadedOrganizationsRef = useRef(new Set());
  const sessionInitializationInProgress = useRef(false);
  const endSessionRef = useRef(null);


  // Clear activity timeout
  const clearActivityTimeout = useCallback(() => {
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
      activityTimeoutRef.current = null;
    }
  }, []);

  // End current session
  const endSession = useCallback(async (isAutoLogout = false) => {
    if (currentSession && user) {
      try {
        // Track logout event
        await AnalyticsService.trackLogout(
          user.uid, 
          currentSession, 
          userOrganizations, 
          isAutoLogout
        );
        
        // End the session
        await AnalyticsService.endSession(currentSession, isAutoLogout);
        
        // Clear session state
        setCurrentSession(null);
      } catch (error) {
        console.error('Error ending session:', error);
      }
    }
  }, [currentSession, user, userOrganizations]);

  // Start monitoring for user inactivity (Firebase auth logout, independent of analytics session)
  const startActivityMonitoring = useCallback(() => {
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }

    const timeoutId = setTimeout(async () => {
      try {
        if (endSessionRef.current) {
          await endSessionRef.current(true);
        }
        await signOut(auth);
      } catch (error) {
        console.error('Error during auto-logout:', error);
        try {
          await signOut(auth);
        } catch (signOutError) {
          console.error('Error during force logout:', signOutError);
        }
      }
    }, AnalyticsService.SESSION_TIMEOUT);

    activityTimeoutRef.current = timeoutId;
  }, []);

  // Initialize or resume analytics session for the authenticated user
  const initializeAnalyticsSession = useCallback(async (authUser, organizationIds) => {
    if (sessionInitializationInProgress.current || !authUser) {
      return null;
    }

    sessionInitializationInProgress.current = true;

    try {
      await authUser.getIdToken();
      const sessionId = await AnalyticsService.initializeSession(authUser.uid, organizationIds);
      if (sessionId) {
        setCurrentSession(sessionId);
      }
      return sessionId;
    } catch (error) {
      console.error('Error initializing analytics session:', error);
      return null;
    } finally {
      sessionInitializationInProgress.current = false;
    }
  }, []);

  // Track user activity and reset auto-logout timer
  const trackActivity = useCallback(async () => {
    const now = Date.now();

    clearActivityTimeout();
    startActivityMonitoring();

    if (!currentSession) {
      return;
    }

    try {
      if (now - lastActivityRef.current > 30000) {
        await AnalyticsService.updateSessionActivity(currentSession);
        lastActivityRef.current = now;
      }
    } catch (error) {
      console.error('Error in trackActivity:', error);
    }
  }, [currentSession, clearActivityTimeout, startActivityMonitoring]);

  // Add activity listeners while authenticated
  useEffect(() => {
    if (user) {
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

      const handleActivity = () => {
        if (!handleActivity.lastCall || Date.now() - handleActivity.lastCall > 1000) {
          handleActivity.lastCall = Date.now();
          trackActivity();
        }
      };

      events.forEach((event) => {
        document.addEventListener(event, handleActivity, true);
      });

      return () => {
        events.forEach((event) => {
          document.removeEventListener(event, handleActivity, true);
        });
      };
    }
  }, [user, trackActivity]);

  // Start inactivity monitoring whenever the user is authenticated
  useEffect(() => {
    if (user) {
      startActivityMonitoring();
    } else {
      clearActivityTimeout();
    }
  }, [user, startActivityMonitoring, clearActivityTimeout]);

  // Firebase token refresh - prevent token expiration for active users
  useEffect(() => {
    if (!user) return;
    
    // Refresh Firebase token every 50 minutes (before the 1-hour expiration)
    // This ensures active users don't get logged out due to token expiration
    const tokenRefreshInterval = setInterval(async () => {
      try {
        await user.getIdToken(true); // Force refresh
        console.log('✅ Firebase token refreshed');
      } catch (error) {
        console.error('❌ Token refresh failed:', error);
      }
    }, 50 * 60 * 1000); // 50 minutes
    
    return () => clearInterval(tokenRefreshInterval);
  }, [user]);

  // Session validation and recovery
  useEffect(() => {
    const validateSession = () => {
      if (user && userRole !== null && !currentSession) {
        initializeAnalyticsSession(user, userOrganizations || []);
      } else if (!user && currentSession) {
        setCurrentSession(null);
        clearActivityTimeout();
      }
    };

    const validationInterval = setInterval(validateSession, 5 * 60 * 1000);

    return () => clearInterval(validationInterval);
  }, [user, userRole, currentSession, userOrganizations, initializeAnalyticsSession, clearActivityTimeout]);

  // Initialize analytics session after auth and profile data load
  useEffect(() => {
    if (user && !loading && userRole !== null && !currentSession) {
      initializeAnalyticsSession(user, userOrganizations || []);
    }
  }, [user, loading, userRole, currentSession, userOrganizations, initializeAnalyticsSession]);

  /**
   * Apply a successful users/{uid} snapshot to AuthContext state.
   * A missing document is a real new user (e.g. Google SSO); a thrown error is not.
   */
  const applyUserSnapshot = useCallback(async (userDoc) => {
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const role = userData.role || 'student';
      setUserRole(role);
      setUserOrganizations(userData.organizationIds || []);
      setCurrentOrganizationId(userData.currentOrganizationId || null);

      if (role === 'admin' && userData.organizationIds && userData.organizationIds.length > 0) {
        await loadReportingData(userData.organizationIds);
      }
    } else {
      setUserRole('student');
      setUserOrganizations([]);
      setCurrentOrganizationId(null);
    }
  }, [loadReportingData]);

  /**
   * Load the Firestore profile for an authenticated user. Keeps the loading
   * spinner up until the read succeeds or retries are exhausted.
   */
  const loadProfileForUser = useCallback(async (authUser) => {
    const fetchId = ++profileFetchIdRef.current;
    setLoading(true);
    setProfileError(false);
    setUserRole(null);
    setUserOrganizations([]);
    setCurrentOrganizationId(null);

    try {
      const userDoc = await fetchUserProfileDocument(authUser, (uid) =>
        getDoc(doc(db, 'users', uid))
      );
      if (fetchId !== profileFetchIdRef.current) return;
      await applyUserSnapshot(userDoc);
      setProfileError(false);
    } catch (error) {
      if (fetchId !== profileFetchIdRef.current) return;
      console.error('Error fetching user data:', error);
      setProfileError(true);
    } finally {
      if (fetchId === profileFetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [applyUserSnapshot]);

  const loadProfileForUserRef = useRef(loadProfileForUser);
  loadProfileForUserRef.current = loadProfileForUser;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        await loadProfileForUserRef.current(user);
      } else {
        profileFetchIdRef.current += 1;
        if (endSessionRef.current) {
          await endSessionRef.current();
        }
        setCurrentSession(null);
        setUserRole(null);
        setUserOrganizations([]);
        setCurrentOrganizationId(null);
        setProfileError(false);
        setReportingData({});
        sessionStorage.removeItem('storykin_reporting_data');
        loadedOrganizationsRef.current.clear();
        sessionInitializationInProgress.current = false;
        setLoading(false);
      }
    });

    return () => {
      profileFetchIdRef.current += 1;
      unsubscribe();
      // Clear activity timeout directly
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
        activityTimeoutRef.current = null;
      }
    };
  }, []);

  endSessionRef.current = endSession;

  const logout = async () => {
    try {
      await endSession(false); // End session before signing out
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  /**
   * Re-fetch user document from Firestore and update AuthContext state.
   * Used after CompleteRegistration and when a profile load error is retried.
   */
  const refreshUserData = useCallback(async () => {
    if (!user) return;
    await loadProfileForUser(user);
  }, [user, loadProfileForUser]);

  const value = {
    user,
    userRole,
    userOrganizations,
    currentOrganizationId,
    currentSession,
    loading,
    profileError,
    logout,
    trackActivity,
    refreshUserData,
    // New reporting data functions
    reportingData,
    getReportingData,
    refreshReportingData,
    loadReportingData
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
